import React, { useRef, useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import CSVReader from "react-csv-reader";
import classNames from "classnames";
import numural from "numeral";
import { format } from "date-fns";
import Select from "react-select";

// COMPONENTS
import ContainerContent from "../Container/ContainerContent";
import LoadingPage from "../Loading/LoadingPage";
import LoadingMutation from "../Loading/LoadingMutation";
import AccruedInterestTable from "./AccruedInterestTable";

// HOOKS
import useBroadCastId from "../../hooks/useBroadCastId";
import useLoading from "../../hooks/useLoading";

// AWS
import { S3Image } from "aws-amplify-react";
import { API, Auth, Storage } from "aws-amplify";
import AWS from "aws-sdk";

// UTILS
import { getCurrentLocalDateTime } from "../../utils/getLocalDateTime";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const sizeColor = 100;

const list_color = ["bg-yellow", "bg-blue", "bg-gray", "bg-pink"].map(
  (color) => {
    return `${color}-${sizeColor}`;
  }
);

const LoadingUpdate = () => {
  return (
    <svg
      className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-200"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx={12}
        cy={12}
        r={10}
        stroke="currentColor"
        strokeWidth={4}
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

function AccruedInterest() {
  const params = useParams();
  const router = useHistory();
  const [loading, setLoading] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingSap, setLoadingSap] = useState(false);
  const [accrued, setAccrued] = useState([]);
  const [optionsPeriod, setOptionsPeriod] = useState([]);
  const [optionSelect, setOptionSelect] = useState({
    value: "",
    label: "",
  });
  const [optionSelectLine, setOptionSelectLine] = useState({
    value: "",
    label: "",
  });
  const [originalData, setOriginalData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const importOptions = {
    header: true,
    dynamicTyping: true,
    skipEmptyLines: true,
  };

  const handleImport = async (importDatas, fileInfo) => {
    try {
      setLoading(true);
      let lastColor = null;
      for (const importData of importDatas) {
        if (importData.job_no) {
          const data = await API.get(
            "MoneyHubAdmin",
            `/accruedInterest/${importData.job_no}`
          );

          if (!data.items.length) continue;

          const find_period = data.items.map((item) => parseInt(item.period));
          const max_period = Math.max(...find_period);

          const keep_options = new Array(max_period)
            .fill(null)
            .map((_, index) => {
              return {
                value: index + 1,
                label: index + 1,
              };
            });

          let new_list_color = [...list_color];

          if (lastColor) {
            new_list_color = list_color.filter(
              (color, index) => color !== lastColor
            );
          }

          const random_number = Math.floor(
            Math.random() * new_list_color.length - 1
          );

          let new_data = [...data.items];

          let activeColor = new_list_color[random_number];

          lastColor = activeColor;

          new_data = new_data.map((item) => {
            return {
              ...item,
              color: activeColor,
            };
          });

          setOptionsPeriod(keep_options);
          setAccrued((prev) => [...prev, ...new_data]);
          setOriginalData((prev) => [...prev, ...new_data]);
        }
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const onChangeSelectPeriod = (e) => {
    if (!accrued.length) return;

    let filter_data = [...originalData];
    if (e.value !== "All") {
      filter_data = originalData.filter((item) => {
        let check_current = item.period.toString() === e.value.toString();
        let check_line =
          item.isFirst.toString() === optionSelectLine.value.toString();
        if (optionSelectLine.value && optionSelectLine.value !== "All") {
          return check_current && check_line;
        } else {
          return check_current;
        }
      });
    }

    setAccrued(filter_data);
    setOptionSelect(e);
  };

  const onChangeSelectLine = (e) => {
    if (!accrued.length) return;

    let filter_data = [...originalData];
    if (e.value !== "All") {
      filter_data = originalData.filter((item) => {
        let check_current = item.isFirst.toString() === e.value.toString();
        let check_period =
          item.period.toString() === optionSelect.value.toString();
        if (optionSelect.value && optionSelect.value !== "All") {
          return check_current && check_period;
        } else {
          return check_current;
        }
      });
    }

    setAccrued(filter_data);
    setOptionSelectLine(e);
  };

  const onClickSubmit = async () => {
    try {
      setLoadingUpdate(true);
      for (const item of accrued) {
        const data = await API.put(
          "MoneyHubAdmin",
          `/accruedInterest/${item._id}`,
          {
            body: {
              accrued_interest: item,
            },
          }
        );

        setAccrued((prev) => {
          const find_index_data = prev.findIndex(
            (data) => data._id === item._id
          );

          if (find_index_data !== -1) {
            prev[find_index_data].color = "bg-green-200";
            prev[find_index_data].ACCR_INTEREST_RECEIVABLE =
              item.fixed_accr_interest_receivable;
          }
          return [...prev];
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoadingUpdate(false);
    }
  };

  const onClickSendSap = async () => {
    try {
      setLoadingSap(true);
      const data = await API.post("MoneyHubAdmin", `/accruedInterest/sendSap`, {
        body: {
          accrued_interests: accrued,
        },
      });

      const returnData = data.returnData;
      setAccrued(returnData);
    } catch (error) {
      console.log("send sap error", error);
    } finally {
      setLoadingSap(false);
    }
  };

  const handleImportAging = async (importDatas, fileInfo) => {
    try {
      for (const importData of importDatas) {
        if (importData.job_no) {
          await API.post(
            "MoneyHubAdmin",
            `/loan/pastHoldAging/${importData.job_no}`
          );
        }
      }
      console.log("importDatas", importDatas);
    } catch (error) {
      console.log("import aging error", error);
    }
  };

  return (
    <ContainerContent>
      <div className="w-full px-4 py-4 relative overflow-hidden">
        <CSVReader
          parserOptions={importOptions}
          onFileLoaded={handleImport}
        ></CSVReader>
        {/* <CSVReader
          parserOptions={importOptions}
          onFileLoaded={handleImportAging}
        ></CSVReader> */}
        <div className=" mt-4">
          <div className="flex items-center w-full space-x-2 z-50">
            <div>เลือกงวด</div>
            <div className="w-52 z-50">
              <Select
                options={[{ label: "All", value: "All" }, ...optionsPeriod]}
                value={optionSelect}
                onChange={onChangeSelectPeriod}
              />
            </div>
          </div>

          <div className="flex items-center w-full space-x-2 mt-2 z-40">
            <div>เลือกไลน์</div>
            <div className="w-52 z-40">
              <Select
                options={[
                  { label: "All", value: "All" },
                  { label: "เส้นแรก", value: "true" },
                  { label: "เส้นที่สอง", value: "false" },
                ]}
                value={optionSelectLine}
                onChange={onChangeSelectLine}
              />
            </div>
          </div>

          <button
            disabled={loadingUpdate}
            onClick={onClickSubmit}
            className={classNames(
              "px-4 py-2 mt-3 mb-2 duration-300 transition-alle rounded shadow text-white flex items-center",
              {
                "hover:bg-green-600 bg-green-500 cursor-pointer":
                  !loadingUpdate,
                "bg-gray-400 cursor-not-allowed": loadingUpdate,
              }
            )}
          >
            {loadingUpdate && <LoadingUpdate />} Submit
          </button>
          <button
            disabled={loadingSap}
            onClick={onClickSendSap}
            className={classNames(
              "px-4 py-2 mt-3 mb-2 duration-300 transition-alle rounded shadow text-white flex items-center",
              {
                "hover:bg-green-600 bg-green-500 cursor-pointer": !loadingSap,
                "bg-gray-400 cursor-not-allowed": loadingSap,
              }
            )}
          >
            {loadingSap && <LoadingUpdate />} Send Sap
          </button>
        </div>
        <div className="mt-3">
          <AccruedInterestTable
            data={accrued}
            setSelectedRows={setSelectedRows}
          />
          <div className="flex items-center justify-center w-full mt-3">
            {loading && <LoadingUpdate />}
          </div>
        </div>
      </div>
    </ContainerContent>
  );
}

export default AccruedInterest;
